import React, { createContext, useContext, useEffect, useState } from "react";
import { AtticusScreenFlow } from "./models/AtticusScreenFlow";
import { User } from "./models/User";
import { Estate } from "./models/Estate";

import {
  SCREEN_PATHS,
  LARAVEL_APP_HOST,
  AUTH_ERRORS,
  DISABLED_PATHS,
  STORAGE,
  UPGRADE_REQUIRED,
} from "./constants";
import { authApi, estateApi, formsApi, geographiesApi } from "./apis";
import { AtticusMySQL } from "./apis/AtticusMySQL";
import { useNavigate, useLocation } from "react-router-dom";
import * as flows from "./views/flows";
import { Box } from "@mui/material";
import { AtticusCircularProgress } from "./views/shared/AtticusCircularProgress";
import { Language } from "./models/Langauges";
import { Subscription } from "./models/Subscription";
import {
  Country,
  County,
  CountyType,
  CourtType,
  State,
} from "./models/Geography";
import { AxiosError } from "axios";
import { getQueryStringParams } from "@theatticusapp/atticus-shared-components";
import { FormType } from "models/FormType";
import { LeadContext } from "LeadContextWrapper";


interface Props {
  children: JSX.Element;
}

interface AuthContextInterface {
  coreDataIsLoading: boolean;
  flow?: AtticusScreenFlow;
  setFlow: (flow?: AtticusScreenFlow) => void;
  user?: User;
  setUser: (user?: User) => void;
  refreshEstates: () => void;
  estates?: Estate[] | null;
  setEstates: (estates?: Estate[] | null) => void;
  activeEstate?: Estate;
  setActiveEstate: (estate?: Estate) => void;
  formTypes?: FormType[];
  states?: State[];
  counties?: County[];
  countyTypes?: CountyType[];
  courtTypes?: CourtType[];
  countries?: Country[];
  languages?: Language[];
  subscription?: Subscription;
  showContent: boolean;
  setShowContent: (showContent: boolean) => void;
  signOutUser: () => void;
  upgradeRequiredReason?: string;
  setUpgradeRequiredReason: (reason: string) => void;
  leadMigrated: boolean;
  leadMigrationError?: string;
  doneLoadingActiveEstate: boolean;
}

const AuthContext = createContext<AuthContextInterface | null>(null);

enum StartUpTasks {
  preloadData = "preloadData",
  fetchEstates = "fetchEstates",
  recordForms = "recordForms",
}

// intentionally not using state as async nature causes problems with tasks tracking
let authTasks = [
  StartUpTasks.preloadData,
  StartUpTasks.fetchEstates,
  StartUpTasks.recordForms,
];

export const AuthContextWrapper = ({ children }: Props): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lead, doneLoadingLead } = useContext(LeadContext)

  // forms of interest may come from sessionStorage - created pre-auth
  //  or they may come as query string params to any screen
  const params = getQueryStringParams();
  const formIdsFromQS = params?.formIds?.split(",") || [];
  const formIdsFromStorageStr = window.sessionStorage.getItem(
    STORAGE.formInterest
  );
  const formIdsFromStorage =
    (formIdsFromStorageStr && JSON.parse(formIdsFromStorageStr)) || [];
  const formsOfInterest = [...formIdsFromQS, ...formIdsFromStorage];
  const [coreDataIsLoading, setCoreDataIsLoading] = useState<boolean>(true);
  const [flow, setFlow] = useState<AtticusScreenFlow | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [estates, setEstates] = useState<Estate[] | undefined | null>(
    undefined
  );
  const [activeEstate, setActiveEstate] = useState<Estate | undefined>(
    undefined
  );

  const [countries, setCountries] = useState<Country[] | undefined>(undefined);
  const [counties, setCounties] = useState<County[] | undefined>(undefined);
  const [countyTypes, setCountyTypes] = useState<CountyType[] | undefined>(
    undefined
  );
  const [courtTypes, setCourtTypes] = useState<CourtType[] | undefined>(
    undefined
  );
  const [states, setStates] = useState<State[] | undefined>(undefined);
  const [formTypes, setFormTypes] = useState<FormType[] | undefined>();
  const [languages, setLanguages] = useState<Language[] | undefined>(undefined);
  const [subscription, setSubscription] = useState<Subscription | undefined>(
    undefined
  );
  // Used to determine when all context has been loaded in, used to trigger route checking.
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  // page content will be hidden until it's determined the user won't be redirect to the Vue app
  const [showContent, setShowContent] = useState<boolean>(false);

  const [upgradeRequiredReason, setUpgradeRequiredReason] = useState<
    string | undefined
  >();
  const [leadMigrated, setLeadMigrated] = useState<boolean>(false)
  const [leadMigrationError, setLeadMigrationError] = useState<string|undefined>()
  const [doneLoadingActiveEstate, setDoneLoadingActiveEstate] = useState<boolean>(false)


  // On all initial renders, check to see if the user is authenticated
  useEffect(() => {
    (async () => {
      await authenticate();
    })();
  }, []);

  // Whenever all data is loaded, check to see if the user's route is permitted
  useEffect(() => {
    if (dataLoaded && doneLoadingLead) {
      checkRouting();
    }
  }, [dataLoaded, doneLoadingLead]);

  // If the user changes, try to fetch the states and estates
  useEffect(() => {
    (async () => {
      if (user) {
        setCoreDataIsLoading(true);
        try {
          const [
            countries,
            countyTypes,
            courtTypes,
            states,
            languages,
            subscription,
            formTypes,
            _estates,
          ] = await Promise.all([
            geographiesApi.fetchCountries(),
            geographiesApi.fetchCountyTypes(),
            geographiesApi.fetchCourtTypes(),
            geographiesApi.fetchStates(),
            AtticusMySQL.getLanguages(),
            AtticusMySQL.getSubscription(),
            formsApi.fetchFormTypes(),
            fetchEstates(), // Sets state within fetchEstates()
          ]);

          setCountries(countries);
          setCounties(counties);
          setCountyTypes(countyTypes);
          setCourtTypes(courtTypes);
          setStates(states);
          setLanguages(languages);
          setSubscription(subscription);
          setFormTypes(formTypes);
          setDataLoaded(true);
        } catch (e) {
          console.error(
            "AuthContextWrapper failed to load core data, err=" + e
          );
        } finally {
          setCoreDataIsLoading(false);
        }
      }
    })(); 
  }, [user]);

  // once we have an active estate, record forms of interest
  useEffect(() => {
    (async () => {
      if (activeEstate?.id) {
        recordFormsOfInterest();
      }
    })();
  }, [activeEstate]);

  const markTaskComplete = (taskName: StartUpTasks) => {
    authTasks = authTasks.filter(
      (task: string) => task !== taskName.toString()
    );
    // once all startup tasks are complete checkRouting
    if (authTasks?.length === 0) {
      checkRouting();
    }
  };

  // Sign the user out of the application, includes authApi signOut().
  const signOutUser = async (): Promise<void> => {
    try {
      setUser?.(undefined);
      setEstates?.(undefined);

      // Visiting /sign-out page below automatically hits authApi.signOut(), destroying the session
      navigate(SCREEN_PATHS.signOut);
    } catch (error) {
      // logger.error(error);
    }
  };

  const authenticate = async (): Promise<void> => {
    if (user === undefined) {
      try {
        const myUser = await authApi.getUser()
        setUser(myUser || undefined);
      } catch (error) {
        if (error == AUTH_ERRORS.laravelNoData) {
          navigate(`${SCREEN_PATHS.signOut}?error=group`, {
            replace: true,
            state: {
              path: location.pathname,
              search: location.search,
              hash: location.hash,
            },
          });
        } else if (error == AUTH_ERRORS.getUserFailed) {
          navigate(SCREEN_PATHS.auth, {
            replace: true,
            state: {
              path: location.pathname,
              search: location.search,
              hash: location.hash,
            },
          });
        } else if (error == AUTH_ERRORS.cognitoNotAuthed) {
          navigate(SCREEN_PATHS.auth, { 
            replace: true,
            state: {
              path: location.pathname,
              search: location.search,
              hash: location.hash,
            },
          });
        } else {
          navigate(SCREEN_PATHS.signOut);
        }
      }
    }
  };

  const refreshEstates = async (): Promise<void> => {
    await fetchEstates();
  };

  const preloadData = async (): Promise<void> => {
    Promise.all([
      await AtticusMySQL.getLanguages(),
      await AtticusMySQL.getSubscription(),
    ]).then(([languages, subscription]) => {
      setLanguages(languages);
      setSubscription(subscription);
      markTaskComplete(StartUpTasks.preloadData);
    });
  };

  useEffect(() => {
    (async () => {
      if (user) {
        preloadData(); // don't await to allow simultaneous call to fetchEstates
        if (estates === undefined) {
          await fetchEstates();
        }
        setDataLoaded(true);
      }
    })();
  }, [user]);

  const fetchEstates = async (): Promise<void> => {
    const fetchedEstates = await estateApi.fetchEstates();

    // we have an activeEstate
    if (fetchedEstates?.length) {
      setEstates(fetchedEstates);
      fetchedEstates[0].assets = await estateApi.getAssets() // For some reason this pulls more things than using an estate id
      setActiveEstate(fetchedEstates[0]);
    } else {
      // if no estates, activeEstate won't be set
      //  must mark recordForms complete here
      markTaskComplete(StartUpTasks.recordForms);
    }
    markTaskComplete(StartUpTasks.fetchEstates);
    setDoneLoadingActiveEstate(true)
  };

  const recordFormsOfInterest = async (): Promise<void> => {
    if (formsOfInterest?.length && activeEstate?.id) {
      for (let i = 0; i < formsOfInterest?.length; i++) {
        const formId = parseInt(formsOfInterest[i], 10);
        try {
          // console.log("addFormForEstate", formId);
          await formsApi.addFormForEstate(formId, activeEstate.id, true);
        } catch (error) {
          if ((error as AxiosError)?.response?.status === 402) {
            setUpgradeRequiredReason(UPGRADE_REQUIRED.maxForms);
          }
        }
      }
      // clear the storage item so they're not added again
      window.sessionStorage.removeItem(STORAGE.formInterest);
    }
    markTaskComplete(StartUpTasks.recordForms);
  };


  /**
   * Checks the current user's URL route to determine if they should be
   * redirected to another part of the app.
   *
   * When Redirects should occur:
   *    requested any path (other than on-boarding) when on-boarding not complete   => on-boarding
   *    requested a disabled flow                                                   => Vue home
   *    requested on-boarding and yet it's marked complete                          => Vue home
   *    requested a disabled path (constants DISABLED_PATHS)                        => Vue home
   *    requested Forms or Probate Packet paths when AG doesn't allow           => Vue home
   *    unrecognized paths will be sent to 404 (via react-router)                   => 404
   */
  const checkRouting = (): void => {
    const requestingFlow = location.pathname.includes(SCREEN_PATHS.flow, 0);
    
   // if on-boarding is incomplete go to on-boarding
   if (!user?.hasCompletedOnboarding) {
    const requestingOnBoarding = location.pathname.substr(0, SCREEN_PATHS.onBoarding.length) === SCREEN_PATHS.onBoarding;
    if (!requestingOnBoarding) {
      goToOnBoarding(true);
      return;
    }
  }

    // if requesting a flow check if customer should be in there
    if (requestingFlow) {
      // requesting a disabled flow?
      const flowPath = location.pathname.split("/")[2];
      const flow = Object.values(flows).find(
        (flow) => flow.flowPath === flowPath
      );
      const flowDisabled = flow?.disabled;

      // requesting on-boarding when it's complete
      const preventOnBoarding = !!lead?.key || (
        location.pathname.includes(SCREEN_PATHS.onBoarding) &&
        user?.hasCompletedOnboarding)

      if (flowDisabled || preventOnBoarding) {
        goToVueAppHome();
        return;
      }
    }

    // If route is forms or probate packet determination, check if AG allows it.
    const requestingFormsOrProbatePacket = location.pathname
      .split("/")[2]
      .toLowerCase()
      .includes("forms-and-files");
    if (requestingFormsOrProbatePacket) {
      const formsDisabled = activeEstate?.advisorGroup?.formsDisabled;
      if (formsDisabled) {
        goToVueAppHome();
        return;
      }
    }

    // requesting a disabled path? redirect to vue
    if (DISABLED_PATHS.includes(location.pathname)) {
      goToVueAppHome();
      return;
    }

    // -----------------------------------------------------------------------------
    //  IF YOU MAKE IT THIS FAR, SHOW CONTENT TO USER
    // -----------------------------------------------------------------------------

    setShowContent(true);
  };

  /**
   * Redirects the user to the Vue App's Home screen
   */
  const goToVueAppHome = (): void => {
    window.location.href = `${LARAVEL_APP_HOST}/home`;
  };

  /**
   * Take the user to the Onboarding Flow.
   * If a user is already in the Onboarding flow, display the screen they are accessing
   * If a user is not already in the Onboarding flow, route them to the first screen of Onboarding.
   */
  const goToOnBoarding = (forceFirst?: boolean): void => {
    const alreadyInOnboardingFlow = location.pathname.includes(
      SCREEN_PATHS.onBoarding,
      0
    );

    if (!alreadyInOnboardingFlow || forceFirst) {
      navigate(flows.onBoardingFlow.getFlowFirstScreen());
    }
    setShowContent(true);
  };

  return (
    <AuthContext.Provider
      value={{
        coreDataIsLoading,
        flow,
        setFlow,
        user,
        setUser,
        refreshEstates,
        estates,
        setEstates,
        activeEstate,
        setActiveEstate,
        doneLoadingActiveEstate,
        formTypes,
        states,
        counties,
        countyTypes,
        courtTypes,
        countries,
        languages,
        subscription,
        showContent,
        setShowContent,
        signOutUser,
        upgradeRequiredReason,
        setUpgradeRequiredReason,
        leadMigrated,
        leadMigrationError,
      }}
    >
      {showContent && children}
      {!showContent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <AtticusCircularProgress />
        </Box>
      )}
    </AuthContext.Provider>
  );
};

export function useAuthContext():AuthContextInterface {
  const authContext = useContext(AuthContext)
  if(!authContext) {
    throw new Error("the component calling this auth context is not nested within a context provider")
  }

  return authContext
}